import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Icone } from '@/components/ui/Icone'
import * as decouverte from '@/lib/api/decouverte'

/**
 * L'invitation à découvrir une fonctionnalité — F22 §7, [DECISIONS §366].
 *
 * ── POURQUOI UNE CARTE DANS LE FIL, ET RIEN D'AUTRE ────────────────────────
 *
 * Pas une fenêtre modale, pas une bulle qui suit le doigt, pas une bannière
 * collée en haut. Trois raisons, et la troisième décide :
 *
 * 1. **Le Fil est le seul écran que tout le monde ouvre.** Une invitation
 *    posée ailleurs ne serait vue que par ceux qui explorent déjà — c'est-à-dire
 *    exactement ceux qui n'en ont pas besoin.
 * 2. **Une carte se fait défiler.** Elle ne réclame ni clic ni fermeture, et le
 *    refus silencieux — passer sans y toucher — est une réponse que le serveur
 *    sait déjà lire : il compte les invitations SERVIES, pas celles qui ont
 *    reçu un geste.
 * 3. **Le candidat paie ses données à la mégaoctet.** Une carte est du texte et
 *    une icône. Une visite guidée serait un moteur de plus à télécharger, sur
 *    une connexion qui se compte.
 *
 * ── CE QU'ELLE PORTE ───────────────────────────────────────────────────────
 *
 * Ce que c'est, ce que ça lui apporte, un bouton qui **y mène directement**, et
 * un « pas maintenant » qui compte comme refus. Rien de plus : une carte qui
 * expliquerait serait un tutoriel, et un tutoriel ne se lit pas.
 */
export function CarteDeDecouverte({ onOuvrir }: { onOuvrir: (onglet: string) => void }) {
  const client = useQueryClient()

  /*
   * **Une seule fois par consultation, et c'est une contrainte du SERVEUR.**
   *
   * `GET /decouverte` écrit : il note l'invitation servie, et c'est cette date
   * qui tient la cadence. Le rejouer à chaque montage en brûlerait une par
   * aller-retour dans l'application. `staleTime: Infinity` et
   * `refetchOnWindowFocus: false` le retiennent ; la clé ne porte rien de
   * variable, donc un rechargement de page — et lui seul — en redemande une.
   */
  const invitation = useQuery({
    queryKey: ['decouverte'],
    queryFn: decouverte.invitationCourante,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const refuser = useMutation({
    mutationFn: (feature: string) => decouverte.refuserLInvitation(feature),
    onSettled: () => {
      // On efface plutôt qu'on n'invalide : sans données, la carte disparaît
      // tout de suite, et rien ne la redemandera avant le prochain chargement.
      client.setQueryData(['decouverte'], null)
    },
  })

  const proposee = invitation.data

  if (proposee == null) {
    return null
  }

  return (
    <article
      className="mx-4 my-3 flex flex-col gap-3 rounded-2xl p-4"
      style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--bordure)',
      }}
      aria-label={`Découvrir : ${proposee.titre}`}
    >
      <div className="flex items-center gap-2">
        <Icone nom="cible" taille={18} />
        <h3 className="text-[15px] font-semibold" style={{ color: 'var(--texte-1)' }}>
          {proposee.titre}
        </h3>
      </div>

      <p className="text-[15px] leading-snug" style={{ color: 'var(--texte-2)' }}>
        {proposee.promesse}
      </p>

      <div className="flex items-center gap-2">
        <button
          type="button"
          className="rounded-full px-4 py-2 text-[14px] font-semibold"
          style={{ background: 'var(--accent)', color: 'var(--sur-accent)' }}
          onClick={() => {
            // Y MENER, pas expliquer comment y aller. Une invitation qui se
            // termine par « va dans le menu » n'est pas une invitation.
            onOuvrir(proposee.destination)
          }}
        >
          Voir
        </button>

        <button
          type="button"
          className="rounded-full px-4 py-2 text-[14px]"
          style={{ color: 'var(--texte-2)' }}
          onClick={() => {
            refuser.mutate(proposee.feature)
          }}
        >
          Pas maintenant
        </button>
      </div>
    </article>
  )
}
